import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileDown, TrendingUp, PieChart, FileText, Download, Eye, File, ArrowUpDown, ArrowUp, ArrowDown, Copy, Check, Send, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeGreek } from '../utils/greekUtils';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import { useTheme } from '../contexts/ThemeContext';
import { MACHINE_COLORS, generateSerialColorMap } from '../utils/machineColors';
import MegasoftModal from '../components/MegasoftModal';
import { isFsSupported, pickDir, loadDirHandle, ensurePermission, writeFileToDir, listDir, writeTestFile, downloadFile } from '../utils/fsAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_GR = {
  1: "ΙΑΝΟΥΑΡΙΟΣ", 2: "ΦΕΒΡΟΥΑΡΙΟΣ", 3: "ΜΑΡΤΙΟΣ", 4: "ΑΠΡΙΛΙΟΣ",
  5: "ΜΑΙΟΣ", 6: "ΙΟΥΝΙΟΣ", 7: "ΙΟΥΛΙΟΣ", 8: "ΑΥΓΟΥΣΤΟΣ",
  9: "ΣΕΠΤΕΜΒΡΙΟΣ", 10: "ΟΚΤΩΒΡΙΟΣ", 11: "ΝΟΕΜΒΡΙΟΣ", 12: "ΔΕΚΕΜΒΡΙΟΣ"
};

const REPORT_TYPES = [
  "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ",
  "🧾 ΚΑΤΑΣΤΑΣΗ ΤΙΜΟΛΟΓΙΩΝ ΑΝΑ ΜΗΝΑ",
  "💶 ΠΑΓΙΑ ΑΝΑ ΠΕΛΑΤΗ & ΜΗΧΑΝΗΜΑ",
  "💼 ΑΠΟΣΒΕΣΗ ΑΡ.Χ ΑΝΑ ΜΗΧΑΝΗΜΑ",
  "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΚΑΤΑΝΑΛΩΣΕΩΝ",
  "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΜΕΤΡΗΤΩΝ",
  "ΣΕΛΙΔΕΣ ΑΝΑ ΜΑΡΚΑ / ΜΟΝΤΕΛΟ / ΤΥΠΟ / ΧΡΩΜΑ",
  "ΧΡΗΣΗ ΑΝΑΛΩΣΙΜΩΝ & ΑΝΤΑΛΛΑΚΤΙΚΩΝ",
  "ΑΝΑΛΥΤΙΚΗ ΜΗΝΙΑΙΑ ΑΝΑΦΟΡΑ",
  "ΟΙΚΟΝΟΜΙΚΑ ΣΤΑΤΙΣΤΙΚΑ (ΣΥΓΚΡΙΣΗ ΕΤΩΝ)",
  "💰 ΚΕΡΔΟΦΟΡΙΑ ΑΝΑ ΠΕΛΑΤΗ",
  "💰 ΚΕΡΔΟΦΟΡΙΑ ΑΝΑ ΜΗΧΑΝΗΜΑ",
  "📦 ΑΝΑΛΩΣΙΜΑ ΑΝΑ ΠΕΛΑΤΗ",
  "🔧 SERVICE ΑΝΑ ΜΗΧΑΝΗΜΑ",
  "📋 ΛΗΞΗ ΣΥΜΒΟΛΑΙΩΝ",
  "🏆 TOP ΠΕΛΑΤΕΣ (ΕΣΟΔΑ)",
  "📊 ΓΡΑΦΗΜΑ ΤΑΣΗΣ ΣΕΛΙΔΩΝ (ΜΗΝΙΑΙΟ)",
  "📊 ΚΑΤΑΝΟΜΗ ΣΕΛΙΔΩΝ ΑΝΑ ΜΑΡΚΑ (ΠΙΤΑ)",
  "📊 ΚΑΤΑΝΟΜΗ ΑΞΙΑΣ ΑΝΑ ΠΕΛΑΤΗ (ΠΙΤΑ)",
  "📊 ΣΥΓΚΡΙΣΗ BLACK VS COLOR (ΜΠΑΡΕΣ)",
  "📊 TOP 10 ΜΗΧΑΝΗΜΑΤΑ ΚΑΤΑΝΑΛΩΣΗΣ",
  "📊 ΚΟΣΤΟΣ ΑΝΑΛΩΣΙΜΩΝ TREND"
];

const COLORS = ['#004a99', '#0066cc', '#3399ff', '#66b3ff', '#99ccff', '#ccebff', '#ff6b6b', '#ee5a6f', '#f06595', '#cc5de8'];

// Row component for the "Πάγια ανά Πελάτη & Μηχάνημα" report with expand/collapse per customer
const MonthlyFeeCustomerRow = ({ customer, idx }) => {
  const [open, setOpen] = useState(false);
  const bg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
  return (
    <>
      <tr className={`${bg} cursor-pointer hover:bg-blue-50`} onClick={() => setOpen(o => !o)} data-testid={`mf-row-${customer.customer_id}`}>
        <td className="border p-2 text-center text-lg font-bold">{open ? '▼' : '▶'}</td>
        <td className="border p-2 font-medium">{customer.customer_name}</td>
        <td className="border p-2 font-mono text-xs">{customer.customer_afm || '-'}</td>
        <td className="border p-2 text-center">{customer.machines.length}</td>
        <td className="border p-2 text-center font-bold text-blue-700">{customer.total_months}</td>
        <td className="border p-2 text-right font-bold text-emerald-700">{customer.total_amount.toFixed(2)}€</td>
      </tr>
      {open && customer.machines.map((m, i) => (
        <tr key={m.machine_id} className="bg-blue-50/40" data-testid={`mf-machine-${m.machine_id}`}>
          <td className="border p-2"></td>
          <td className="border p-2 pl-8 text-sm text-gray-600" colSpan={2}>
            <span className="text-blue-700">🖨</span> {m.brand} {m.model} <span className="font-mono text-xs text-gray-500">({m.serial})</span>
          </td>
          <td className="border p-2 text-center text-sm">{m.price_per_month.toFixed(2)}€/μήνα</td>
          <td className="border p-2 text-center text-sm">{m.billing_months}</td>
          <td className="border p-2 text-right text-sm">{m.amount.toFixed(2)}€</td>
        </tr>
      ))}
    </>
  );
};

// Greek translations for report column headers
const COLUMN_TRANSLATIONS = {
  'customer': 'ΠΕΛΑΤΗΣ',
  'brand': 'ΜΑΡΚΑ',
  'model': 'ΜΟΝΤΕΛΟ',
  'serial': 'ΣΕΙΡΙΑΚΟΣ',
  'type': 'ΤΥΠΟΣ',
  'total': 'ΣΥΝΟΛΟ',
  'value': 'ΑΞΙΑ (€)',
  'category': 'ΚΑΤΗΓΟΡΙΑ',
  'machines': 'ΜΗΧΑΝΗΜΑΤΑ',
  'black_a4': 'ΜΑΥΡΟ A4',
  'color_a4': 'ΕΓΧΡΩΜΟ A4',
  'black_a3': 'ΜΑΥΡΟ A3',
  'color_a3': 'ΕΓΧΡΩΜΟ A3',
  'name': 'ΟΝΟΜΑ',
  'consumable_type': 'ΤΥΠΟΣ ΑΝΑΛΩΣΙΜΟΥ',
  'dispatches': 'ΑΠΟΣΤΟΛΕΣ',
  'total_quantity': 'ΣΥΝΟΛ. ΠΟΣΟΤΗΤΑ',
  'total_cost': 'ΣΥΝΟΛ. ΚΟΣΤΟΣ (€)',
  'machine': 'ΜΗΧΑΝΗΜΑ',
  'contract_cost': 'ΠΑΓΙΟ (€)',
  'prev_date': 'ΠΡΟΗΓ. ΗΜΕΡ.',
  'prev_black_a4': 'ΠΡΟΗΓ. ΜΑΥΡΟ A4',
  'curr_date': 'ΤΡΕΧ. ΗΜΕΡ.',
  'curr_black_a4': 'ΤΡΕΧ. ΜΑΥΡΟ A4',
  'diff_black_a4': 'ΔΙΑΦ. ΜΑΥΡΟ A4',
  'prev_color_a4': 'ΠΡΟΗΓ. ΕΓΧΡ. A4',
  'curr_color_a4': 'ΤΡΕΧ. ΕΓΧΡ. A4',
  'diff_color_a4': 'ΔΙΑΦ. ΕΓΧΡ. A4',
  'prev_black_a3': 'ΠΡΟΗΓ. ΜΑΥΡΟ A3',
  'curr_black_a3': 'ΤΡΕΧ. ΜΑΥΡΟ A3',
  'diff_black_a3': 'ΔΙΑΦ. ΜΑΥΡΟ A3',
  'prev_color_a3': 'ΠΡΟΗΓ. ΕΓΧΡ. A3',
  'curr_color_a3': 'ΤΡΕΧ. ΕΓΧΡ. A3',
  'diff_color_a3': 'ΔΙΑΦ. ΕΓΧΡ. A3',
  'year': 'ΕΤΟΣ',
  'pages_black': 'ΣΕΛΙΔΕΣ ΜΑΥΡΟ',
  'pages_color': 'ΣΕΛΙΔΕΣ ΕΓΧΡΩΜΟ',
  'total_pages': 'ΣΥΝΟΛΟ ΣΕΛΙΔΩΝ',
  'month': 'ΜΗΝΑΣ',
  'month_name': 'ΜΗΝΑΣ',
  'black': 'ΜΑΥΡΟ',
  'color': 'ΕΓΧΡΩΜΟ',
  'cost': 'ΚΟΣΤΟΣ (€)',
  'pages': 'ΣΕΛΙΔΕΣ',
  'consumables_qty': 'ΠΟΣ. ΑΝΑΛΩΣΙΜΩΝ',
  'parts_qty': 'ΠΟΣ. ΑΝΤΑΛΛΑΚΤΙΚΩΝ',
  'consumables_cost': 'ΚΟΣΤΟΣ ΑΝΑΛΩΣ. (€)',
  'parts_cost': 'ΚΟΣΤΟΣ ΑΝΤ/ΚΩΝ (€)',
  'total_cost': 'ΣΥΝΟΛ. ΚΟΣΤΟΣ (€)',
  'service_count': 'ΠΛΗΘΟΣ SERVICE',
  'labor_cost': 'ΚΟΣΤΟΣ ΕΡΓΑΣΙΑΣ (€)',
  'revenue_pages': 'ΕΣΟΔΑ ΣΕΛΙΔΩΝ (€)',
  'revenue_monthly': 'ΕΣΟΔΑ ΠΑΓΙΩΝ (€)',
  'expense_consumables': 'ΕΞΟΔΑ ΑΝΑΛΩΣ. (€)',
  'expense_service': 'ΕΞΟΔΑ SERVICE (€)',
  'total_revenue': 'ΣΥΝΟΛΟ ΕΣΟΔΩΝ (€)',
  'total_expenses': 'ΣΥΝΟΛΟ ΕΞΟΔΩΝ (€)',
  'profit': 'ΚΕΡΔΟΣ (€)',
  'revenue': 'ΕΣΟΔΑ (€)',
  'expenses': 'ΕΞΟΔΑ (€)',
  'contract_start': 'ΕΝΑΡΞΗ ΣΥΜΒ.',
  'contract_end': 'ΛΗΞΗ ΣΥΜΒ.',
  'days_left': 'ΗΜΕΡΕΣ ΕΩΣ ΛΗΞΗ',
  'status': 'ΚΑΤΑΣΤΑΣΗ',
  'monthly_fee': 'ΠΑΓΙΟ (€)',
};

// Translate a column key to Greek
const translateColumn = (col) => {
  return COLUMN_TRANSLATIONS[col] || col.toUpperCase();
};

const ReportsPage = () => {
  const [reportType, setReportType] = useState(REPORT_TYPES[0]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [fromYear, setFromYear] = useState(new Date().getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [toMonth, setToMonth] = useState(new Date().getMonth() + 1);
  const [useRange, setUseRange] = useState(false);
  const [yearPrev, setYearPrev] = useState(new Date().getFullYear() - 1);
  const [customerId, setCustomerId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [groupBy, setGroupBy] = useState('brand');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [superReportData, setSuperReportData] = useState(null);
  const [invoicedCustomerIds, setInvoicedCustomerIds] = useState(new Set());
  const [reportTableSort, setReportTableSort] = useState({ key: null, direction: null });
  const { currentColors } = useTheme();
  const reportRef = useRef(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // MEGASOFT accounting send
  const [megasoftOpen, setMegasoftOpen] = useState(false);
  const [megasoftLoading, setMegasoftLoading] = useState(false);
  const [megasoftPreview, setMegasoftPreview] = useState(null);
  const [megasoftResults, setMegasoftResults] = useState(null);
  const [megasoftSending, setMegasoftSending] = useState(false);
  const [megasoftSettings, setMegasoftSettings] = useState(null);
  const [megasoftHistory, setMegasoftHistory] = useState([]);
  const [dirHandle, setDirHandle] = useState(null);
  const [folderName, setFolderName] = useState('');

  const copyLine = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Αντιγράφηκε!');
      setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1500);
    } catch (e) {
      // Fallback for older browsers / non-secure contexts
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedKey(key);
        toast.success('Αντιγράφηκε!');
        setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1500);
      } catch (err) {
        toast.error('Αποτυχία αντιγραφής');
      }
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setReportData(null);
    setSuperReportData(null);
    setReportTableSort({ key: null, direction: null });
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let params = {};

      // Build range params
      const rangeParams = useRange ? { from_year: fromYear, from_month: fromMonth, to_year: toYear, to_month: toMonth } : {};

      switch (reportType) {
        case "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ":
          endpoint = '/reports/super-report';
          params = { year: useRange ? fromYear : year, month: useRange ? fromMonth : month, customer_id: customerId, ...rangeParams };
          break;
        case "🧾 ΚΑΤΑΣΤΑΣΗ ΤΙΜΟΛΟΓΙΩΝ ΑΝΑ ΜΗΝΑ":
          endpoint = '/invoices/by-month';
          params = { year: useRange ? fromYear : year, month: useRange ? fromMonth : month };
          break;
        case "💶 ΠΑΓΙΑ ΑΝΑ ΠΕΛΑΤΗ & ΜΗΧΑΝΗΜΑ":
          endpoint = '/reports/monthly-fees-summary';
          // Force range mode params - default to "Jan of fromYear .. current month of toYear"
          params = {
            from_year: useRange ? fromYear : year,
            from_month: useRange ? fromMonth : 1,
            to_year: useRange ? toYear : year,
            to_month: useRange ? toMonth : 12,
            customer_id: customerId,
          };
          break;
        case "💼 ΑΠΟΣΒΕΣΗ ΑΡ.Χ ΑΝΑ ΜΗΧΑΝΗΜΑ":
          endpoint = '/reports/arx-amortization';
          params = { year: useRange ? fromYear : year };
          break;
        case "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΚΑΤΑΝΑΛΩΣΕΩΝ":
          endpoint = '/reports/annual-consumption';
          params = { year: useRange ? fromYear : year, customer_id: customerId, ...rangeParams };
          break;
        case "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΜΕΤΡΗΤΩΝ":
          endpoint = '/reports/annual-counters';
          params = { year: useRange ? fromYear : year, customer_id: customerId, ...rangeParams };
          break;
        case "ΣΕΛΙΔΕΣ ΑΝΑ ΜΑΡΚΑ / ΜΟΝΤΕΛΟ / ΤΥΠΟ / ΧΡΩΜΑ":
          endpoint = '/reports/pages-by-category';
          params = { year: useRange ? fromYear : year, group_by: groupBy, ...rangeParams };
          break;
        case "ΧΡΗΣΗ ΑΝΑΛΩΣΙΜΩΝ & ΑΝΤΑΛΛΑΚΤΙΚΩΝ":
          endpoint = '/reports/consumables-usage';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "ΑΝΑΛΥΤΙΚΗ ΜΗΝΙΑΙΑ ΑΝΑΦΟΡΑ":
          endpoint = '/reports/monthly-detail';
          params = { year: useRange ? fromYear : year, month: useRange ? fromMonth : month, ...rangeParams };
          break;
        case "ΟΙΚΟΝΟΜΙΚΑ ΣΤΑΤΙΣΤΙΚΑ (ΣΥΓΚΡΙΣΗ ΕΤΩΝ)":
          endpoint = '/reports/financial-comparison';
          params = { year_current: year, year_previous: yearPrev, ...rangeParams };
          break;
        case "📊 ΓΡΑΦΗΜΑ ΤΑΣΗΣ ΣΕΛΙΔΩΝ (ΜΗΝΙΑΙΟ)":
          endpoint = '/reports/monthly-trend';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "📊 ΚΑΤΑΝΟΜΗ ΣΕΛΙΔΩΝ ΑΝΑ ΜΑΡΚΑ (ΠΙΤΑ)":
          endpoint = '/reports/brand-distribution';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "📊 ΚΑΤΑΝΟΜΗ ΑΞΙΑΣ ΑΝΑ ΠΕΛΑΤΗ (ΠΙΤΑ)":
          endpoint = '/reports/customer-value';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "📊 ΣΥΓΚΡΙΣΗ BLACK VS COLOR (ΜΠΑΡΕΣ)":
          endpoint = '/reports/black-vs-color';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "📊 TOP 10 ΜΗΧΑΝΗΜΑΤΑ ΚΑΤΑΝΑΛΩΣΗΣ":
          endpoint = '/reports/top-machines';
          params = { year: useRange ? fromYear : year, limit: 10, ...rangeParams };
          break;
        case "📊 ΚΟΣΤΟΣ ΑΝΑΛΩΣΙΜΩΝ TREND":
          endpoint = '/reports/consumables-cost-trend';
          params = { year: useRange ? fromYear : year, ...rangeParams };
          break;
        case "💰 ΚΕΡΔΟΦΟΡΙΑ ΑΝΑ ΠΕΛΑΤΗ":
          endpoint = '/reports/profitability-by-customer';
          params = { year: useRange ? fromYear : year };
          break;
        case "💰 ΚΕΡΔΟΦΟΡΙΑ ΑΝΑ ΜΗΧΑΝΗΜΑ":
          endpoint = '/reports/profitability-by-machine';
          params = { year: useRange ? fromYear : year };
          break;
        case "📦 ΑΝΑΛΩΣΙΜΑ ΑΝΑ ΠΕΛΑΤΗ":
          endpoint = '/reports/consumables-by-customer';
          params = { year: useRange ? fromYear : year };
          break;
        case "🔧 SERVICE ΑΝΑ ΜΗΧΑΝΗΜΑ":
          endpoint = '/reports/service-by-machine';
          params = { year: useRange ? fromYear : year };
          break;
        case "📋 ΛΗΞΗ ΣΥΜΒΟΛΑΙΩΝ":
          endpoint = '/reports/contracts-expiring';
          params = {};
          break;
        case "🏆 TOP ΠΕΛΑΤΕΣ (ΕΣΟΔΑ)":
          endpoint = '/reports/top-customers';
          params = { year: useRange ? fromYear : year, limit: 10 };
          break;
        default:
          toast.error('Μη έγκυρος τύπος αναφοράς');
          return;
      }

      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (reportType === "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ") {
        setSuperReportData(response.data);
        // Fetch invoice status for this month so we can overlay "ΤΙΜΟΛΟΓΗΜΕΝΟ" on invoiced customer cards
        try {
          const invResp = await axios.get(`${API_URL}/invoices/by-month`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { year: response.data.year, month: response.data.month }
          });
          const ids = new Set((invResp.data.items || []).filter(i => i.is_issued).map(i => i.customer_id));
          setInvoicedCustomerIds(ids);
        } catch (_e) {
          setInvoicedCustomerIds(new Set());
        }
      } else if (reportType === "🧾 ΚΑΤΑΣΤΑΣΗ ΤΙΜΟΛΟΓΙΩΝ ΑΝΑ ΜΗΝΑ") {
        // Adapt invoice status response into the standard {data: [...]} shape used by renderInvoiceStatusTable
        setReportData({
          ...response.data,
          data: response.data.items || []
        });
      } else if (reportType === "💶 ΠΑΓΙΑ ΑΝΑ ΠΕΛΑΤΗ & ΜΗΧΑΝΗΜΑ") {
        // Wrap minimal for the gate; renderMonthlyFeesReport reads .customers
        setReportData({ ...response.data, data: response.data.customers || [] });
      } else if (reportType === "💼 ΑΠΟΣΒΕΣΗ ΑΡ.Χ ΑΝΑ ΜΗΧΑΝΗΜΑ") {
        setReportData({ ...response.data, data: response.data.items || [] });
      } else {
        setReportData(response.data);
      }
      toast.success('Η αναφορά δημιουργήθηκε επιτυχώς!');
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης αναφοράς: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData || !reportData.data || reportData.data.length === 0) {
      toast.error('Δεν υπάρχουν δεδομένα για εξαγωγή');
      return;
    }

    try {
      const ws = XLSX.utils.json_to_sheet(reportData.data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Αναφορά');
      XLSX.writeFile(wb, `report_${year}.xlsx`);
      toast.success('Το αρχείο Excel κατέβηκε επιτυχώς!');
    } catch (error) {
      toast.error('Σφάλμα εξαγωγής Excel: ' + error.message);
    }
  };

  const renderFilters = () => {
    const isComparison = reportType === "ΟΙΚΟΝΟΜΙΚΑ ΣΤΑΤΙΣΤΙΚΑ (ΣΥΓΚΡΙΣΗ ΕΤΩΝ)";
    
    return (
      <div className="space-y-4 mb-6">
        {/* Date range toggle */}
        {!isComparison && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useRange}
                onChange={(e) => setUseRange(e.target.checked)}
                className="w-4 h-4 rounded"
                data-testid="use-range-toggle"
              />
              <span className="text-sm font-medium text-gray-700">ΕΥΡΟΣ ΗΜΕΡΟΜΗΝΙΩΝ</span>
            </label>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {!useRange && !isComparison && (
            <>
              {/* Single year */}
              <div>
                <Label>ΕΤΟΣ</Label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                />
              </div>

              {/* Single month - for specific reports */}
              {(reportType === "ΑΝΑΛΥΤΙΚΗ ΜΗΝΙΑΙΑ ΑΝΑΦΟΡΑ" || reportType === "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ" || reportType === "🧾 ΚΑΤΑΣΤΑΣΗ ΤΙΜΟΛΟΓΙΩΝ ΑΝΑ ΜΗΝΑ") && (
                <div>
                  <Label>ΜΗΝΑΣ</Label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {Object.entries(MONTHS_GR).map(([num, name]) => (
                      <option key={num} value={num}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {/* Date range selectors */}
          {useRange && !isComparison && (
            <>
              <div>
                <Label>ΑΠΟ ΜΗΝΑ</Label>
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="from-month"
                >
                  {Object.entries(MONTHS_GR).map(([num, name]) => (
                    <option key={num} value={num}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>ΑΠΟ ΕΤΟΣ</Label>
                <Input
                  type="number"
                  value={fromYear}
                  onChange={(e) => setFromYear(parseInt(e.target.value))}
                  data-testid="from-year"
                />
              </div>
              <div>
                <Label>ΕΩΣ ΜΗΝΑ</Label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="to-month"
                >
                  {Object.entries(MONTHS_GR).map(([num, name]) => (
                    <option key={num} value={num}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>ΕΩΣ ΕΤΟΣ</Label>
                <Input
                  type="number"
                  value={toYear}
                  onChange={(e) => setToYear(parseInt(e.target.value))}
                  data-testid="to-year"
                />
              </div>
            </>
          )}

          {/* Comparison years */}
          {isComparison && (
            <>
              <div>
                <Label>ΤΡΕΧΟΝ ΕΤΟΣ</Label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                />
              </div>
              <div>
                <Label>ΠΡΟΗΓΟΥΜΕΝΟ ΕΤΟΣ</Label>
                <Input
                  type="number"
                  value={yearPrev}
                  onChange={(e) => setYearPrev(parseInt(e.target.value))}
                />
              </div>
            </>
          )}

          {/* Customer filter */}
          {(reportType === "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΚΑΤΑΝΑΛΩΣΕΩΝ" || reportType === "ΕΤΗΣΙΑ ΑΝΑΦΟΡΑ ΜΕΤΡΗΤΩΝ" || reportType === "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ" || reportType === "💶 ΠΑΓΙΑ ΑΝΑ ΠΕΛΑΤΗ & ΜΗΧΑΝΗΜΑ") && (
            <div>
              <Label>ΠΕΛΑΤΗΣ</Label>
              <select
                value={customerId || ''}
                onChange={(e) => setCustomerId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">ΟΛΟΙ ΟΙ ΠΕΛΑΤΕΣ</option>
                {customers.map(cust => (
                  <option key={cust.id} value={cust.id}>{cust.company_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Group by filter */}
          {reportType === "ΣΕΛΙΔΕΣ ΑΝΑ ΜΑΡΚΑ / ΜΟΝΤΕΛΟ / ΤΥΠΟ / ΧΡΩΜΑ" && (
            <div>
              <Label>ΟΜΑΔΟΠΟΙΗΣΗ</Label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="brand">ΜΑΡΚΑ</option>
                <option value="model">ΜΟΝΤΕΛΟ</option>
                <option value="machine_type">ΤΥΠΟΣ</option>
                <option value="machine_color">ΧΡΩΜΑ</option>
              </select>
            </div>
          )}

          {/* Generate button */}
          <div className="flex items-end">
            <Button 
              onClick={fetchReport} 
              disabled={loading} 
              className="w-full btn-primary text-white"
              style={{ backgroundColor: currentColors.primary }}
              data-testid="generate-report-btn"
            >
              {loading ? 'Φόρτωση...' : 'ΔΗΜΙΟΥΡΓΙΑ ΑΝΑΦΟΡΑΣ'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    if (reportType === "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ" && superReportData) {
      return renderSuperReport();
    }
    
    if (!reportData) return null;

    if (reportType === "🧾 ΚΑΤΑΣΤΑΣΗ ΤΙΜΟΛΟΓΙΩΝ ΑΝΑ ΜΗΝΑ") {
      return renderInvoiceStatusTable();
    }

    if (reportType === "💶 ΠΑΓΙΑ ΑΝΑ ΠΕΛΑΤΗ & ΜΗΧΑΝΗΜΑ") {
      return renderMonthlyFeesReport();
    }

    if (reportType === "💼 ΑΠΟΣΒΕΣΗ ΑΡ.Χ ΑΝΑ ΜΗΧΑΝΗΜΑ") {
      return renderArxAmortizationReport();
    }

    const isChartReport = reportType.startsWith("📊");

    if (isChartReport) {
      return renderChart();
    } else {
      return renderTable();
    }
  };

  // Render invoice status table with toggle actions (mark / unmark)
  const renderInvoiceStatusTable = () => {
    if (!reportData || !reportData.data) return null;
    const rows = reportData.data;
    const yr = reportData.year;
    const mo = reportData.month;
    const issued = reportData.issued_count || 0;
    const pending = reportData.pending_count || 0;
    const monthName = MONTHS_GR[mo];

    const toggleInvoice = async (row) => {
      const token = localStorage.getItem('token');
      try {
        if (row.is_issued) {
          await axios.delete(`${API_URL}/invoices/${row.customer_id}/${yr}/${mo}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Διαγράφηκε τιμολόγιο: ${row.company_name}`);
        } else {
          await axios.post(`${API_URL}/invoices/mark`, {
            customer_id: row.customer_id, year: yr, month: mo
          }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`Καταγράφηκε τιμολόγιο: ${row.company_name}`);
        }
        const resp = await axios.get(`${API_URL}/invoices/by-month`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: yr, month: mo }
        });
        setReportData({ ...resp.data, data: resp.data.items || [] });
      } catch (error) {
        toast.error('Σφάλμα: ' + (error.response?.data?.detail || error.message));
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 rounded-lg bg-green-100 border border-green-300 text-green-800 font-semibold" data-testid="invoice-issued-stat">
            ✅ ΕΚΔΟΘΗΚΑΝ: {issued}
          </div>
          <div className="px-4 py-2 rounded-lg bg-red-100 border border-red-300 text-red-800 font-semibold" data-testid="invoice-pending-stat">
            ⏳ ΕΚΚΡΕΜΟΥΝ: {pending}
          </div>
          <div className="px-4 py-2 rounded-lg bg-blue-100 border border-blue-300 text-blue-800 font-semibold">
            ΜΗΝΑΣ: {monthName} {yr}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse border" data-testid="invoice-status-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 text-center">ΚΑΤΑΣΤΑΣΗ</th>
                <th className="border p-2 text-left">ΠΕΛΑΤΗΣ</th>
                <th className="border p-2 text-left">ΑΦΜ</th>
                <th className="border p-2 text-left">ΤΗΛΕΦΩΝΟ</th>
                <th className="border p-2 text-left">ΑΡ. ΤΙΜΟΛΟΓΙΟΥ</th>
                <th className="border p-2 text-right">ΠΟΣΟ</th>
                <th className="border p-2 text-center">ΕΝΕΡΓΕΙΑ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.customer_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border p-2 text-center">
                    {row.is_issued ? (
                      <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 font-bold">✅ ΕΚΔΟΘΗΚΕ</span>
                    ) : (
                      <span className="inline-block px-2 py-1 rounded bg-red-100 text-red-800 font-bold">⏳ ΕΚΚΡΕΜΕΙ</span>
                    )}
                  </td>
                  <td className="border p-2 font-medium">{row.company_name}</td>
                  <td className="border p-2 font-mono">{row.afm || '-'}</td>
                  <td className="border p-2">{row.phone || '-'}</td>
                  <td className="border p-2 font-mono">{row.invoice_number || '-'}</td>
                  <td className="border p-2 text-right">{row.amount > 0 ? row.amount.toFixed(2) + '€' : '-'}</td>
                  <td className="border p-2 text-center">
                    <Button
                      size="sm"
                      onClick={() => toggleInvoice(row)}
                      data-testid={`toggle-invoice-${row.customer_id}`}
                      className={row.is_issued ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                    >
                      {row.is_issued ? 'ΑΚΥΡΩΣΗ' : 'ΣΗΜΕΙΩΣΗ'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Mark invoice as issued for the customers in current super report (filtered by customerId or all)
  const handleMarkInvoice = async () => {
    if (!superReportData || !superReportData.reports || superReportData.reports.length === 0) {
      toast.error('Δεν υπάρχει αναφορά για να σημειωθεί τιμολόγιο');
      return;
    }
    const yr = superReportData.year;
    const mo = superReportData.month;
    // Group reports by customer to derive unique customer list & total amount per customer
    const byCustomer = {};
    superReportData.reports.forEach(r => {
      if (!r.customer_id) return;
      if (!byCustomer[r.customer_id]) {
        byCustomer[r.customer_id] = { customer_id: r.customer_id, customer: r.customer, total: 0 };
      }
      byCustomer[r.customer_id].total += (r.final_total || 0);
    });
    const customers = Object.values(byCustomer);
    if (customers.length === 0) {
      toast.error('Δεν εντοπίστηκαν πελάτες στην αναφορά');
      return;
    }
    const monthName = MONTHS_GR[mo];
    const confirmMsg = customers.length === 1
      ? `Επιβεβαίωση τιμολόγησης για:\n${customers[0].customer}\n${monthName} ${yr}\nΣύνολο: ${customers[0].total.toFixed(2)}€\n\nΣυνέχεια;`
      : `Επιβεβαίωση τιμολόγησης για ${customers.length} πελάτες (${monthName} ${yr}).\nΣυνέχεια;`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      let okCount = 0;
      for (const c of customers) {
        await axios.post(
          `${API_URL}/invoices/mark`,
          { customer_id: c.customer_id, year: yr, month: mo, amount: parseFloat(c.total.toFixed(2)) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        okCount++;
      }
      toast.success(`Καταγράφηκε τιμολόγιο για ${okCount} πελάτες (${monthName} ${yr})`);
      // Refresh invoiced customer IDs so watermark shows immediately
      try {
        const invResp = await axios.get(`${API_URL}/invoices/by-month`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: yr, month: mo }
        });
        const ids = new Set((invResp.data.items || []).filter(i => i.is_issued).map(i => i.customer_id));
        setInvoicedCustomerIds(ids);
      } catch (_e) { /* non-fatal */ }
    } catch (error) {
      toast.error('Σφάλμα καταγραφής τιμολογίου: ' + (error.response?.data?.detail || ''));
    }
  };

  // ===== MEGASOFT: open preview, send charges =====
  const handleOpenMegasoft = async () => {
    if (!superReportData || !superReportData.reports || superReportData.reports.length === 0) {
      toast.error('Δημιουργήστε πρώτα το Super Report');
      return;
    }
    setMegasoftOpen(true);
    setMegasoftResults(null);
    setMegasoftPreview(null);
    setMegasoftLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [prevRes, setRes] = await Promise.all([
        axios.get(`${API_URL}/accounting/preview-charges`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: superReportData.year, month: superReportData.month, customer_id: customerId }
        }),
        axios.get(`${API_URL}/accounting/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMegasoftPreview(prevRes.data);
      setMegasoftSettings(setRes.data);
      fetchMegasoftHistory();
    } catch (error) {
      toast.error('Σφάλμα προεπισκόπησης: ' + (error.response?.data?.detail || ''));
    } finally {
      setMegasoftLoading(false);
    }
  };

  const fetchMegasoftHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/accounting/send-history`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: superReportData.year, month: superReportData.month }
      });
      setMegasoftHistory(res.data.items || []);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => {
    loadDirHandle().then(h => { if (h) { setDirHandle(h); setFolderName(h.name); } });
  }, []);

  const handleResendOrder = async (item) => {
    const when = String(item.sent_at || '').slice(0, 16).replace('T', ' ');
    if (!window.confirm(`⚠️ ΠΡΟΣΟΧΗ: Η παραγγελία για «${item.customer_name}» έχει ΗΔΗ σταλεί${when ? ` στις ${when}` : ''}.\n\nΘέλετε σίγουρα να γίνει ΕΠΑΝΑΠΟΣΤΟΛΗ; Θα δημιουργηθεί ξανά η εγγραφή/αρχείο της παραγγελίας.`)) return;
    const method = megasoftPreview?.method || megasoftSettings?.method;
    const token = localStorage.getItem('token');
    try {
      if (method === 'browser_folder') {
        let h = dirHandle;
        if (!h) { toast.error('Επίλεξε πρώτα τον φάκελο inbox (Ρυθμίσεις).'); return; }
        if (!(await ensurePermission(h))) { toast.error('Δεν δόθηκε άδεια στον φάκελο.'); return; }
        const res = await axios.get(`${API_URL}/accounting/export-commands`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: item.year, month: item.month, customer_id: item.customer_id, force: true }
        });
        const files = res.data.files || [];
        const written = [];
        for (const f of files) {
          await writeFileToDir(h, f.name, JSON.stringify(f.content, null, 2));
          written.push({ external_ref: f.external_ref, customer_id: f.customer_id, customer_name: f.customer_name, lines_count: f.lines_count, status: 'queued' });
        }
        if (written.length) {
          await axios.post(`${API_URL}/accounting/mark-sent`, { year: item.year, month: item.month, refs: written }, { headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success(`Επαναποστολή: γράφτηκαν ${written.length} αρχεία στον φάκελο.`);
      } else {
        const res = await axios.post(`${API_URL}/accounting/send-charges`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: item.year, month: item.month, customer_id: item.customer_id, force: true }
        });
        toast.success(`Επαναποστολή: ${res.data.sent_count} παραγγελία(ες).`);
      }
      fetchMegasoftHistory();
    } catch (error) {
      toast.error('Σφάλμα επαναποστολής: ' + (error.response?.data?.detail || error.message || ''));
    }
  };

  const handlePickFolder = async () => {
    if (!isFsSupported()) {
      toast.error('Ο browser δεν υποστηρίζει επιλογή φακέλου. Χρησιμοποίησε Chrome ή Edge.');
      return;
    }
    try {
      const h = await pickDir();
      setDirHandle(h);
      setFolderName(h.name);
      toast.success(`Επιλέχθηκε φάκελος: ${h.name}`);
    } catch (_e) { /* user cancelled */ }
  };

  const handleTestWrite = async () => {
    if (!isFsSupported()) { toast.error('Χρησιμοποίησε Chrome ή Edge.'); return; }
    if (!dirHandle) { toast.error('Επίλεξε πρώτα φάκελο.'); return; }
    if (!(await ensurePermission(dirHandle))) { toast.error('Δεν δόθηκε άδεια στον φάκελο.'); return; }
    try {
      const name = await writeTestFile(dirHandle);
      const present = await listDir(dirHandle, '');
      const list = present.slice(0, 12).join(', ') || '(κενός)';
      toast.success(`✓ Γράφτηκε «${name}» στον «${folderName}». Περιεχόμενα φακέλου (${present.length}): ${list}`, { duration: 9000 });
    } catch (e) {
      toast.error('Αποτυχία δοκιμής εγγραφής: ' + (e.message || e));
    }
  };

  const handleDownloadSend = async () => {
    setMegasoftSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/accounting/export-commands`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: megasoftPreview.year, month: megasoftPreview.month, customer_id: customerId }
      });
      const files = res.data.files || [];
      const skipped = res.data.skipped || [];
      const results = [];
      const written = [];
      for (const f of files) {
        downloadFile(f.name, JSON.stringify(f.content, null, 2));
        written.push({ external_ref: f.external_ref, customer_id: f.customer_id, customer_name: f.customer_name, lines_count: f.lines_count, status: 'queued' });
        results.push({ customer: f.customer_name, status: 'queued', message: `Κατέβηκε: ${f.name}` });
        await new Promise(r => setTimeout(r, 200));
      }
      if (written.length) {
        await axios.post(`${API_URL}/accounting/mark-sent`, { year: megasoftPreview.year, month: megasoftPreview.month, refs: written }, { headers: { Authorization: `Bearer ${token}` } });
      }
      skipped.forEach(sk => results.push({ customer: sk.customer_name, status: 'exists', message: `Έχει ήδη σταλεί (${String(sk.sent_at || '').slice(0, 16).replace('T', ' ')})` }));
      setMegasoftResults({ results, sent_count: written.length, total_orders: files.length + skipped.length, method: 'download' });
      toast.success(`Κατέβηκαν ${written.length} αρχεία. Έλεγξε τον φάκελο Λήψεων του browser.`);
      fetchMegasoftHistory();
    } catch (error) {
      toast.error('Σφάλμα λήψης: ' + (error.response?.data?.detail || error.message || ''));
    } finally {
      setMegasoftSending(false);
    }
  };

  const handleBrowserSend = async () => {
    if (!isFsSupported()) {
      toast.error('Ο browser δεν υποστηρίζει εγγραφή σε τοπικό φάκελο. Χρησιμοποίησε Chrome ή Edge.');
      return;
    }
    let h = dirHandle;
    if (!h) {
      toast.error('Επίλεξε πρώτα τον φάκελο inbox (κουμπί «Ρυθμίσεις»).');
      return;
    }
    const ok = await ensurePermission(h);
    if (!ok) {
      toast.error('Δεν δόθηκε άδεια στον φάκελο. Επίλεξέ τον ξανά στις Ρυθμίσεις.');
      return;
    }
    setMegasoftSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/accounting/export-commands`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: megasoftPreview.year, month: megasoftPreview.month, customer_id: customerId }
      });
      const files = res.data.files || [];
      const skipped = res.data.skipped || [];
      const results = [];
      const written = [];
      for (const f of files) {
        try {
          await writeFileToDir(h, f.name, JSON.stringify(f.content, null, 2));
          written.push({ external_ref: f.external_ref, customer_id: f.customer_id, customer_name: f.customer_name, lines_count: f.lines_count, status: 'queued' });
          results.push({ customer: f.customer_name, status: 'queued', message: `Γράφτηκε: ${f.name}` });
        } catch (e) {
          results.push({ customer: f.customer_name, status: 'error', message: 'Αποτυχία εγγραφής: ' + e.message });
        }
      }
      if (written.length) {
        await axios.post(`${API_URL}/accounting/mark-sent`, { year: megasoftPreview.year, month: megasoftPreview.month, refs: written }, { headers: { Authorization: `Bearer ${token}` } });
      }
      skipped.forEach(sk => results.push({ customer: sk.customer_name, status: 'exists', message: `Έχει ήδη σταλεί (${String(sk.sent_at || '').slice(0, 16).replace('T', ' ')})` }));
      // Verify: list what is actually in the folder now
      let present = [];
      try { present = await listDir(h, '.json'); } catch (_e) { /* ignore */ }
      setMegasoftResults({ results, sent_count: written.length, total_orders: files.length + skipped.length, method: 'browser_folder', folder: folderName, present });
      toast.success(`Γράφτηκαν ${written.length} αρχεία στον φάκελο «${folderName}». Στον φάκελο υπάρχουν τώρα ${present.length} αρχεία .json.`);
      fetchMegasoftHistory();
    } catch (error) {
      toast.error('Σφάλμα αποστολής: ' + (error.response?.data?.detail || error.message || ''));
    } finally {
      setMegasoftSending(false);
    }
  };

  const handleSaveMegasoftSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/accounting/settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Οι ρυθμίσεις αποθηκεύτηκαν!');
      // refresh settings + preview configured flag
      const [prevRes, setRes] = await Promise.all([
        axios.get(`${API_URL}/accounting/preview-charges`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: superReportData.year, month: superReportData.month, customer_id: customerId }
        }),
        axios.get(`${API_URL}/accounting/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMegasoftPreview(prevRes.data);
      setMegasoftSettings(setRes.data);
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης ρυθμίσεων: ' + (error.response?.data?.detail || ''));
    }
  };

  const handleSendMegasoft = async () => {
    if (!megasoftPreview) return;
    const method = megasoftPreview?.method || megasoftSettings?.method;
    if (method === 'browser_folder') {
      return handleBrowserSend();
    }
    if (method === 'download') {
      return handleDownloadSend();
    }
    const orderCount = (megasoftPreview.orders || []).length;
    if (orderCount === 0) {
      toast.error('Δεν υπάρχουν γραμμές προς αποστολή');
      return;
    }
    if (!window.confirm(`Αποστολή ${orderCount} παραγγελιών στο Megasoft (${megasoftPreview.month_name} ${megasoftPreview.year});\n\nΘα δημιουργηθούν εγγραφές «Προς τιμολόγηση». Όσες έχουν ήδη σταλεί θα παραλειφθούν. Συνέχεια;`)) return;
    setMegasoftSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/accounting/send-charges`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: megasoftPreview.year, month: megasoftPreview.month, customer_id: customerId }
      });
      setMegasoftResults(res.data);
      toast.success(`Στάλθηκαν ${res.data.sent_count}/${res.data.total_orders} παραγγελίες`);
      fetchMegasoftHistory();
    } catch (error) {
      toast.error('Σφάλμα αποστολής: ' + (error.response?.data?.detail || ''));
    } finally {
      setMegasoftSending(false);
    }
  };

  // Render monthly-fees report: collapsible per customer with per-machine details
  const renderMonthlyFeesReport = () => {    if (!reportData || !reportData.customers) return null;
    const rng = reportData.range || {};
    const monthName = (n) => MONTHS_GR[n] || n;
    return (
      <div className="space-y-4">
        {/* Summary stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 rounded-lg bg-blue-100 border border-blue-300 text-blue-800 font-semibold" data-testid="mf-stat-range">
            ΠΕΡΙΟΔΟΣ: {monthName(rng.from_month)} {rng.from_year} → {monthName(rng.to_month)} {rng.to_year}
          </div>
          <div className="px-4 py-2 rounded-lg bg-purple-100 border border-purple-300 text-purple-800 font-semibold" data-testid="mf-stat-customers">
            👤 ΠΕΛΑΤΕΣ: {reportData.customers_count}
          </div>
          <div className="px-4 py-2 rounded-lg bg-indigo-100 border border-indigo-300 text-indigo-800 font-semibold" data-testid="mf-stat-machines">
            🖨 ΜΗΧΑΝΗΜΑΤΑ: {reportData.machines_count}
          </div>
          <div className="px-4 py-2 rounded-lg bg-amber-100 border border-amber-300 text-amber-800 font-semibold" data-testid="mf-stat-months">
            📅 ΣΥΝΟΛΟ ΠΑΓΙΩΝ: {reportData.grand_total_months}
          </div>
          <div className="px-4 py-2 rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-800 font-semibold" data-testid="mf-stat-amount">
            💰 ΣΥΝΟΛΟ €: {reportData.grand_total_amount.toFixed(2)}€
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse border" data-testid="monthly-fees-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 w-10"></th>
                <th className="border p-2 text-left">ΠΕΛΑΤΗΣ</th>
                <th className="border p-2 text-left">ΑΦΜ</th>
                <th className="border p-2 text-center">ΜΗΧΑΝΗΜΑΤΑ</th>
                <th className="border p-2 text-center">ΠΑΓΙΑ (μήνες)</th>
                <th className="border p-2 text-right">ΣΥΝΟΛΟ €</th>
              </tr>
            </thead>
            <tbody>
              {reportData.customers.map((c, idx) => (
                <MonthlyFeeCustomerRow key={c.customer_id} customer={c} idx={idx} />
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-200 font-bold">
                <td className="border p-2"></td>
                <td className="border p-2" colSpan={2}>ΣΥΝΟΛΟ ({reportData.customers_count} πελάτες)</td>
                <td className="border p-2 text-center">{reportData.machines_count}</td>
                <td className="border p-2 text-center">{reportData.grand_total_months}</td>
                <td className="border p-2 text-right">{reportData.grand_total_amount.toFixed(2)}€</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // Render ΑΡ.Χ amortization report
  const renderArxAmortizationReport = () => {
    if (!reportData || !reportData.items) return null;
    const { items, totals, counts, year: yr } = reportData;
    const statusBadge = (s) => {
      if (s === 'fully_amortized') return <span className="inline-block px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-bold text-xs">✅ ΑΠΟΣΒΕΣΤΗΚΕ</span>;
      if (s === 'in_progress') return <span className="inline-block px-2 py-1 rounded bg-amber-100 text-amber-800 font-bold text-xs">⏳ ΣΕ ΕΞΕΛΙΞΗ</span>;
      if (s === 'no_lease_date') return <span className="inline-block px-2 py-1 rounded bg-rose-100 text-rose-800 font-bold text-xs" title="Δηλώστε Ημ. Έναρξης Μίσθωσης στο μηχάνημα για υπολογισμό απόσβεσης">⚠ ΧΩΡΙΣ ΗΜ. ΜΙΣΘΩΣΗΣ</span>;
      return <span className="inline-block px-2 py-1 rounded bg-gray-100 text-gray-700 font-bold text-xs">⚪ ΑΝΕΝΕΡΓΟ</span>;
    };
    const fmtDate = (s) => {
      if (!s) return '—';
      const parts = String(s).split('-');
      if (parts.length >= 3) return `${parts[2].slice(0,2)}/${parts[1]}/${parts[0]}`;
      if (parts.length === 2) return `${parts[1]}/${parts[0]}`;
      return s;
    };
    const bar = (pct, color) => (
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div className={`h-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    );
    return (
      <div className="space-y-4">
        {/* Summary badges */}
        <div className="flex flex-wrap gap-3">
          <div className="px-4 py-2 rounded-lg bg-blue-100 border border-blue-300 text-blue-800 font-semibold" data-testid="arx-year">
            ΕΤΟΣ: {yr}
          </div>
          <div className="px-4 py-2 rounded-lg bg-purple-100 border border-purple-300 text-purple-800 font-semibold" data-testid="arx-total-machines">
            🖨 {counts.total_machines} ΜΗΧΑΝΗΜΑΤΑ
          </div>
          <div className="px-4 py-2 rounded-lg bg-amber-100 border border-amber-300 text-amber-800 font-semibold" data-testid="arx-in-progress">
            ⏳ ΣΕ ΕΞΕΛΙΞΗ: {counts.in_progress}
          </div>
          <div className="px-4 py-2 rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-800 font-semibold" data-testid="arx-fully">
            ✅ ΑΠΟΣΒΕΣΘΗΚΑΝ: {counts.fully_amortized}
          </div>
          {counts.no_lease_date > 0 && (
            <div className="px-4 py-2 rounded-lg bg-rose-100 border border-rose-300 text-rose-800 font-semibold" data-testid="arx-no-lease">
              ⚠ ΧΩΡΙΣ ΗΜ. ΜΙΣΘΩΣΗΣ: {counts.no_lease_date}
            </div>
          )}
        </div>

        {/* Grand totals card */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <div>
              <div className="text-xs text-gray-600">Αρχικό Σύνολο ΑΡ.Χ</div>
              <div className="text-lg font-bold text-indigo-700">{totals.original_arx.toFixed(2)}€</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Αποσβ. Προηγ. Έτη</div>
              <div className="text-lg font-bold text-gray-700">{totals.consumed_prev_years.toFixed(2)}€</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Έξοδο Τρ. Έτους</div>
              <div className="text-lg font-bold text-red-600">{totals.this_year_expense.toFixed(2)}€</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Υπόλοιπο</div>
              <div className="text-lg font-bold text-amber-700">{totals.remaining_after.toFixed(2)}€</div>
            </div>
            <div>
              <div className="text-xs text-gray-600">Συν. % Απόσβεσης</div>
              <div className="text-lg font-bold text-emerald-700">{totals.percent_amortized}%</div>
              {bar(totals.percent_amortized, 'bg-emerald-500')}
            </div>
          </div>
        </div>

        {/* Per-machine table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse border" data-testid="arx-table">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 text-left">ΠΕΛΑΤΗΣ</th>
                <th className="border p-2 text-left">ΜΗΧΑΝΗΜΑ</th>
                <th className="border p-2 text-center">S/N</th>
                <th className="border p-2 text-center" title="Ημερομηνία έναρξης μίσθωσης">ΕΝΑΡΞΗ</th>
                <th className="border p-2 text-center" title="Μήνες χρέωσης το έτος αναφοράς">ΜΗΝΕΣ {yr}</th>
                <th className="border p-2 text-right">ΑΡΧΙΚΟ ΑΡ.Χ</th>
                <th className="border p-2 text-right">ΑΠΟΣΒ. ΠΡΟΗΓ.</th>
                <th className="border p-2 text-right">ΕΞΟΔΟ {yr}</th>
                <th className="border p-2 text-right">ΥΠΟΛΟΙΠΟ</th>
                <th className="border p-2 text-center">ΠΡΟΟΔΟΣ</th>
                <th className="border p-2 text-center">ΚΑΤΑΣΤΑΣΗ</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, idx) => (
                <tr key={r.machine_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`arx-row-${r.machine_id}`}>
                  <td className="border p-2 font-medium">{r.customer_name}</td>
                  <td className="border p-2">{r.brand} {r.model}</td>
                  <td className="border p-2 text-center font-mono text-xs">{r.machine_serial}</td>
                  <td className="border p-2 text-center font-mono text-xs whitespace-nowrap">{fmtDate(r.contract_start)}</td>
                  <td className="border p-2 text-center font-semibold">{r.this_year_months}</td>
                  <td className="border p-2 text-right font-bold">{r.original_arx.toFixed(2)}€</td>
                  <td className="border p-2 text-right text-gray-600">{r.consumed_prev_years.toFixed(2)}€</td>
                  <td className="border p-2 text-right text-red-600 font-semibold">{r.this_year_expense.toFixed(2)}€</td>
                  <td className="border p-2 text-right text-amber-700 font-semibold">{r.remaining_after.toFixed(2)}€</td>
                  <td className="border p-2 w-40">
                    <div className="text-xs text-center mb-1 font-semibold">{r.percent_amortized}%</div>
                    {bar(r.percent_amortized,
                      r.percent_amortized >= 100 ? 'bg-emerald-500' :
                      r.percent_amortized >= 50  ? 'bg-blue-500' :
                      r.percent_amortized >= 20  ? 'bg-amber-500' : 'bg-red-400')}
                  </td>
                  <td className="border p-2 text-center">{statusBadge(r.status)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-200 font-bold">
                <td className="border p-2" colSpan={5}>ΣΥΝΟΛΟ ({counts.total_machines} μηχανήματα)</td>
                <td className="border p-2 text-right">{totals.original_arx.toFixed(2)}€</td>
                <td className="border p-2 text-right">{totals.consumed_prev_years.toFixed(2)}€</td>
                <td className="border p-2 text-right text-red-700">{totals.this_year_expense.toFixed(2)}€</td>
                <td className="border p-2 text-right text-amber-800">{totals.remaining_after.toFixed(2)}€</td>
                <td className="border p-2 text-center">{totals.percent_amortized}%</td>
                <td className="border p-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // Generate TXT content for Super Report
  const generateSuperReportTxt = () => {
    if (!superReportData || !superReportData.reports || superReportData.reports.length === 0) {
      toast.error('Δεν υπάρχουν δεδομένα για εξαγωγή');
      return;
    }

    let txtContent = '';
    const separator = '==================================================';
    const line = '--------------------------------------------------';

    superReportData.reports.forEach((report, index) => {
      txtContent += `${superReportData.company_name} - REPORT\n`;
      txtContent += `${separator}\n`;
      txtContent += `ΠΕΛΑΤΗΣ: ${report.customer}\n`;
      txtContent += `S/N: ${report.serial}\n`;
      txtContent += `ΜΗΧΑΝΗΜΑ: ${report.brand} ${report.model}\n`;
      txtContent += `ΜΗΝΑΣ: ${superReportData.month_name} ${superReportData.year}\n`;
      txtContent += `${line}\n\n`;

      // Line items (A4 BLACK, A4 COLOR, etc.)
      report.line_items.forEach(item => {
        txtContent += `${item.type}:\n`;
        txtContent += `  ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${item.prev_date || '-'} - ΣΕΛΙΔΕΣ: ${item.prev_value.toLocaleString()}\n`;
        txtContent += `  ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${item.curr_date || '-'} - ΣΕΛΙΔΕΣ: ${item.curr_value.toLocaleString()}\n`;
        txtContent += `  ΣΥΝΟΛΟ: ${item.curr_value.toLocaleString()} - ${item.prev_value.toLocaleString()} = ${item.diff.toLocaleString()}\n\n`;
      });

      txtContent += '\n';

      // Cost breakdown
      report.line_items.forEach(item => {
        if (item.diff > 0 || item.price > 0) {
          txtContent += `${item.type}: ${item.diff.toLocaleString()} x ${item.price.toFixed(4)}€ = ${item.cost.toFixed(2)}€\n`;
        }
      });

      if (report.monthly_fee > 0) {
        txtContent += `ΠΑΓΙΟ: ${report.monthly_fee.toFixed(2)}€\n`;
      }

      txtContent += `ΚΑΘΑΡΗ ΑΞΙΑ: ${report.net_total.toFixed(2)}€\n`;
      txtContent += `ΤΕΛΙΚΟ (ΜΕ ΦΠΑ ${report.vat_rate}%): ${report.final_total.toFixed(2)}€\n`;
      txtContent += `${separator}\n`;
      txtContent += `ΠΗΓΗ: ${report.reading_method} | ΗΜ/ΝΙΑ: ${superReportData.generated_date}\n`;
      txtContent += `${separator}\n\n\n`;
    });

    // Grand totals
    txtContent += `ΓΕΝΙΚΑ ΣΥΝΟΛΑ\n`;
    txtContent += `${separator}\n`;
    txtContent += `ΣΥΝΟΛΟ ΚΑΘΑΡΗΣ ΑΞΙΑΣ: ${superReportData.grand_total_net.toFixed(2)}€\n`;
    txtContent += `ΣΥΝΟΛΟ ΜΕ ΦΠΑ 24%: ${superReportData.grand_total_with_vat.toFixed(2)}€\n`;
    txtContent += `${separator}\n`;

    // Download as TXT
    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SuperReport_${superReportData.month_name}_${superReportData.year}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Το αρχείο TXT κατέβηκε επιτυχώς!');
  };

  // Generate PDF for Super Report using window.print
  const generateSuperReportPdf = () => {
    if (!superReportData || !superReportData.reports || superReportData.reports.length === 0) {
      toast.error('Δεν υπάρχουν δεδομένα για εξαγωγή');
      return;
    }
    window.print();
  };

  // Render Super Report preview
  const renderSuperReport = () => {
    if (!superReportData || !superReportData.reports || superReportData.reports.length === 0) {
      return <div className="text-center py-8 text-gray-500">Δεν βρέθηκαν δεδομένα για αυτόν τον μήνα</div>;
    }

    // Generate color map for serials
    const uniqueSerials = [...new Set(superReportData.reports.map(r => r.serial))];
    const serialColorMap = {};
    uniqueSerials.forEach((serial, idx) => {
      serialColorMap[serial] = MACHINE_COLORS[idx % MACHINE_COLORS.length];
    });

    return (
      <div className="space-y-6">
        {/* Export Buttons */}
        <div className="flex gap-4 flex-wrap">
          <Button onClick={generateSuperReportTxt} className="flex items-center gap-2 bg-green-600 hover:bg-green-700" data-testid="super-report-txt-btn">
            <Download className="h-4 w-4" />
            📥 ΛΗΨΗ TXT
          </Button>
          <Button onClick={generateSuperReportPdf} className="flex items-center gap-2 btn-primary" style={{ backgroundColor: currentColors.primary }} data-testid="super-report-pdf-btn">
            <File className="h-4 w-4" />
            📄 ΛΗΨΗ PDF
          </Button>
          <Button onClick={handleMarkInvoice} className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white" data-testid="super-report-invoice-btn">
            <FileText className="h-4 w-4" />
            🧾 ΤΙΜΟΛΟΓΙΟ
          </Button>
          <Button onClick={handleOpenMegasoft} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="super-report-megasoft-btn">
            <Send className="h-4 w-4" />
            📤 ΑΠΟΣΤΟΛΗ ΣΤΟ ΛΟΓΙΣΤΙΚΟ
          </Button>
        </div>

        {megasoftOpen && (
          <MegasoftModal
            preview={megasoftPreview}
            loading={megasoftLoading}
            sending={megasoftSending}
            results={megasoftResults}
            settings={megasoftSettings}
            onSaveSettings={handleSaveMegasoftSettings}
            history={megasoftHistory}
            onResend={handleResendOrder}
            folderName={folderName}
            onPickFolder={handlePickFolder}
            onTestWrite={handleTestWrite}
            onSend={handleSendMegasoft}
            onClose={() => setMegasoftOpen(false)}
          />
        )}

        {/* Color Legend */}
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <span className="font-medium text-gray-600">Μηχανήματα:</span>
          {uniqueSerials.map((serial, idx) => (
            <span 
              key={serial} 
              className="px-2 py-1 rounded text-xs font-mono"
              style={{ backgroundColor: MACHINE_COLORS[idx % MACHINE_COLORS.length] }}
            >
              {serial}
            </span>
          ))}
        </div>

        {/* Preview - wrapped in ref for PDF export */}
        <div ref={reportRef} className="space-y-6 bg-white p-4">
          {superReportData.reports.map((report, index) => {
            const isInvoiced = report.customer_id && invoicedCustomerIds.has(report.customer_id);
            return (
            <Card
              key={index}
              className="border-2 relative overflow-hidden"
              style={{
                borderColor: currentColors.primary,
                backgroundColor: serialColorMap[report.serial] || '#f9fafb'
              }}
              data-testid={`super-report-card-${report.serial}`}
            >
              {isInvoiced && (
                <div
                  className="pointer-events-none absolute inset-0 flex items-center justify-center z-10 select-none"
                  aria-hidden="true"
                  data-testid={`invoiced-stamp-${report.customer_id}`}
                >
                  <span
                    className="font-black tracking-widest"
                    style={{
                      transform: 'rotate(-22deg)',
                      color: 'rgba(220, 38, 38, 0.28)',
                      border: '6px double rgba(220, 38, 38, 0.35)',
                      padding: '10px 40px',
                      borderRadius: '10px',
                      fontSize: 'clamp(2rem, 6vw, 4rem)',
                      letterSpacing: '0.15em',
                      whiteSpace: 'nowrap',
                      background: 'rgba(255,255,255,0.05)',
                      textShadow: '2px 2px 0 rgba(0,0,0,0.06)',
                    }}
                  >
                    ΤΙΜΟΛΟΓΗΜΕΝΟ
                  </span>
                </div>
              )}
              <CardContent className="p-6 font-mono text-sm relative">
                {/* Header */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold" style={{ color: currentColors.primary }}>{superReportData.company_name} - REPORT</h3>
                  <div className="border-b-2 my-2" style={{ borderColor: currentColors.primary }}></div>
                </div>

                {/* Machine Info */}
                <div className="mb-4 bg-white/80 p-3 rounded border">
                  <p><strong>ΠΕΛΑΤΗΣ:</strong> {report.customer}</p>
                  <p><strong>S/N:</strong> {report.serial}</p>
                  <p><strong>ΜΗΧΑΝΗΜΑ:</strong> {report.brand} {report.model}</p>
                  <p><strong>ΜΗΝΑΣ:</strong> {superReportData.month_name} {superReportData.year}</p>
                </div>

                {/* Line Items */}
                <div className="space-y-4 mb-4">
                  {report.line_items.map((item, idx) => {
                    const prevText = `ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${item.prev_date || '-'} - ΣΕΛΙΔΕΣ: ${item.prev_value.toLocaleString()}`;
                    const currText = `ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${item.curr_date || '-'} - ΣΕΛΙΔΕΣ: ${item.curr_value.toLocaleString()}`;
                    const prevKey = `${report.serial}-${idx}-prev`;
                    const currKey = `${report.serial}-${idx}-curr`;
                    return (
                    <div key={idx} className="bg-white p-3 rounded border">
                      <p className="font-bold" style={{ color: currentColors.primary }}>{item.type}:</p>
                      <div className="ml-4 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => copyLine(prevText, prevKey)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 hover:bg-blue-50 hover:border-blue-400 transition-colors shrink-0"
                          title="Αντιγραφή σειράς"
                          data-testid={`copy-prev-${report.serial}-${idx}`}
                        >
                          {copiedKey === prevKey ? <Check size={14} className="text-green-600" /> : <Copy size={12} className="text-gray-600" />}
                        </button>
                        <p>ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: {item.prev_date || '-'} - ΣΕΛΙΔΕΣ: <span className="font-bold">{item.prev_value.toLocaleString()}</span></p>
                      </div>
                      <div className="ml-4 flex items-center gap-2 mt-1">
                        <button
                          type="button"
                          onClick={() => copyLine(currText, currKey)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 hover:bg-blue-50 hover:border-blue-400 transition-colors shrink-0"
                          title="Αντιγραφή σειράς"
                          data-testid={`copy-curr-${report.serial}-${idx}`}
                        >
                          {copiedKey === currKey ? <Check size={14} className="text-green-600" /> : <Copy size={12} className="text-gray-600" />}
                        </button>
                        <p>ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: {item.curr_date || '-'} - ΣΕΛΙΔΕΣ: <span className="font-bold">{item.curr_value.toLocaleString()}</span></p>
                      </div>
                      <p className="ml-4 text-green-700 font-bold">ΣΥΝΟΛΟ: {item.curr_value.toLocaleString()} - {item.prev_value.toLocaleString()} = {item.diff.toLocaleString()}</p>
                    </div>
                    );
                  })}
                </div>

                {/* Cost Breakdown */}
                <div className="p-4 rounded border mb-4" style={{ backgroundColor: `${currentColors.primary}15`, borderColor: `${currentColors.primary}40` }}>
                  <p className="font-bold mb-2" style={{ color: currentColors.primary }}>ΚΟΣΤΟΛΟΓΗΣΗ:</p>
                  {report.line_items.map((item, idx) => (
                    item.diff > 0 || item.price > 0 ? (
                      <p key={idx}>{item.type}: {item.diff.toLocaleString()} x {item.price.toFixed(4)}€ = <strong>{item.cost.toFixed(2)}€</strong></p>
                    ) : null
                  ))}
                  {report.monthly_fee > 0 && (
                    <p>ΠΑΓΙΟ: <strong>{report.monthly_fee.toFixed(2)}€</strong></p>
                  )}
                </div>

                {/* Totals */}
                <div className="bg-green-50 p-4 rounded border border-green-300">
                  <p className="text-lg">ΚΑΘΑΡΗ ΑΞΙΑ: <strong>{report.net_total.toFixed(2)}€</strong></p>
                  <p className="text-xl font-bold text-green-700">ΤΕΛΙΚΟ (ΜΕ ΦΠΑ {report.vat_rate}%): {report.final_total.toFixed(2)}€</p>
                </div>

                {/* Footer */}
                <div className="mt-4 text-center text-gray-500 text-xs border-t pt-2">
                  ΠΗΓΗ: {report.reading_method} | ΗΜ/ΝΙΑ: {superReportData.generated_date}
                </div>
              </CardContent>
            </Card>
            );
          })}

          {/* Grand Totals */}
          <Card className="text-white" style={{ backgroundColor: currentColors.primary }}>
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4">ΓΕΝΙΚΑ ΣΥΝΟΛΑ</h3>
              <p className="text-lg">ΣΥΝΟΛΟ ΚΑΘΑΡΗΣ ΑΞΙΑΣ: <strong>{superReportData.grand_total_net.toFixed(2)}€</strong></p>
              <p className="text-2xl font-bold">ΣΥΝΟΛΟ ΜΕ ΦΠΑ 24%: {superReportData.grand_total_with_vat.toFixed(2)}€</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const handleReportSort = (columnKey) => {
    setReportTableSort(prev => {
      if (prev.key === columnKey) {
        if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
        if (prev.direction === 'desc') return { key: null, direction: null };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const getReportSortIcon = (columnKey) => {
    if (reportTableSort.key !== columnKey) return <ArrowUpDown className="h-3 w-3 opacity-40 inline ml-1" />;
    if (reportTableSort.direction === 'asc') return <ArrowUp className="h-3 w-3 inline ml-1" />;
    return <ArrowDown className="h-3 w-3 inline ml-1" />;
  };

  const sortReportData = (inputData) => {
    if (!reportTableSort.key || !reportTableSort.direction) return inputData;
    return [...inputData].sort((a, b) => {
      const aVal = a[reportTableSort.key];
      const bVal = b[reportTableSort.key];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = normalizeGreek(String(aVal)).localeCompare(normalizeGreek(String(bVal)), 'el', { numeric: true });
      }
      return reportTableSort.direction === 'asc' ? cmp : -cmp;
    });
  };

  const renderTable = () => {
    if (!reportData.data || reportData.data.length === 0) {
      return <div className="text-center py-8 text-gray-500">Δεν βρέθηκαν δεδομένα</div>;
    }

    const data = reportData.data;
    const columns = Object.keys(data[0]);
    const sortedData = sortReportData(data);

    // Build serial color map if 'serial' column exists
    const hasSerial = columns.includes('serial');
    const serialColorMap = hasSerial ? generateSerialColorMap(data, 'serial') : {};
    const uniqueSerials = hasSerial ? [...new Set(data.map(r => r.serial).filter(Boolean))] : [];

    // Calculate totals row
    const totals = {};
    columns.forEach(col => {
      if (typeof data[0][col] === 'number') {
        totals[col] = data.reduce((sum, row) => sum + (row[col] || 0), 0);
      } else {
        totals[col] = col === columns[0] ? 'ΣΥΝΟΛΑ' : '';
      }
    });

    return (
      <div className="space-y-4">
        {/* Color legend for serials */}
        {hasSerial && uniqueSerials.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center text-sm">
            <span className="font-medium text-gray-600">Μηχανήματα:</span>
            {uniqueSerials.map((serial) => (
              <span 
                key={serial} 
                className="px-2 py-1 rounded text-xs font-mono"
                style={{ backgroundColor: serialColorMap[serial] }}
              >
                {serial}
              </span>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-white" style={{ backgroundColor: currentColors.primary }}>
                {columns.map(col => (
                  <th 
                    key={col} 
                    className="px-4 py-2 text-left text-sm font-semibold border cursor-pointer hover:brightness-110 select-none transition-colors"
                    onClick={() => handleReportSort(col)}
                    data-testid={`report-sort-${col}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {translateColumn(col)}
                      {getReportSortIcon(col)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr 
                  key={idx} 
                  style={{ backgroundColor: hasSerial && row.serial ? serialColorMap[row.serial] || (idx % 2 === 0 ? '#ffffff' : '#f9fafb') : (idx % 2 === 0 ? '#ffffff' : '#f9fafb') }}
                >
                  {columns.map(col => (
                    <td key={col} className="px-4 py-2 text-sm border">
                      {typeof row[col] === 'number' ? row[col].toLocaleString() : row[col]}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Totals row */}
              <tr className="font-bold" style={{ backgroundColor: `${currentColors.primary}20` }}>
                {columns.map(col => (
                  <td key={col} className="px-4 py-2 text-sm border">
                    {typeof totals[col] === 'number' ? totals[col].toLocaleString() : totals[col]}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Export button */}
        <Button onClick={exportToExcel} className="flex items-center gap-2 btn-primary" style={{ backgroundColor: currentColors.primary }}>
          <FileDown className="h-4 w-4" />
          📥 ΛΗΨΗ (Excel)
        </Button>
      </div>
    );
  };

  const renderChart = () => {
    if (!reportData.data || reportData.data.length === 0) {
      return <div className="text-center py-8 text-gray-500">Δεν βρέθηκαν δεδομένα</div>;
    }

    const data = reportData.data;

    switch (reportType) {
      case "📊 ΓΡΑΦΗΜΑ ΤΑΣΗΣ ΣΕΛΙΔΩΝ (ΜΗΝΙΑΙΟ)":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="black" stroke="#000000" name="BLACK" strokeWidth={2} />
              <Line type="monotone" dataKey="color" stroke="#ff6b6b" name="COLOR" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case "📊 ΚΑΤΑΝΟΜΗ ΣΕΛΙΔΩΝ ΑΝΑ ΜΑΡΚΑ (ΠΙΤΑ)":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <RePieChart>
              <Pie
                data={data}
                dataKey="pages"
                nameKey="brand"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={(entry) => `${entry.brand}: ${entry.pages.toLocaleString()}`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </RePieChart>
          </ResponsiveContainer>
        );

      case "📊 ΚΑΤΑΝΟΜΗ ΑΞΙΑΣ ΑΝΑ ΠΕΛΑΤΗ (ΠΙΤΑ)":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <RePieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="customer"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={(entry) => `${entry.customer}: €${entry.value.toLocaleString()}`}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </RePieChart>
          </ResponsiveContainer>
        );

      case "📊 ΣΥΓΚΡΙΣΗ BLACK VS COLOR (ΜΠΑΡΕΣ)":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="serial" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="black" fill="#000000" name="BLACK" />
              <Bar dataKey="color" fill="#ff6b6b" name="COLOR" />
            </BarChart>
          </ResponsiveContainer>
        );

      case "📊 TOP 10 ΜΗΧΑΝΗΜΑΤΑ ΚΑΤΑΝΑΛΩΣΗΣ":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="serial" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_pages" fill="#004a99" name="ΣΥΝΟΛΟ ΣΕΛΙΔΩΝ" />
            </BarChart>
          </ResponsiveContainer>
        );

      case "📊 ΚΟΣΤΟΣ ΑΝΑΛΩΣΙΜΩΝ TREND":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="cost" stroke="#004a99" name="ΚΟΣΤΟΣ €" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">📈 ΑΝΑΦΟΡΕΣ</h1>
          <p className="text-gray-600">Δημιουργία και εξαγωγή αναφορών</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>📊 ΤΥΠΟΣ ΑΝΑΦΟΡΑΣ</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={reportType}
            onChange={(e) => {
              setReportType(e.target.value);
              setReportData(null);
              setSuperReportData(null);
            }}
            className="w-full px-4 py-2 border rounded-md text-lg"
          >
            {REPORT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ΦΙΛΤΡΑ</CardTitle>
        </CardHeader>
        <CardContent>
          {renderFilters()}
        </CardContent>
      </Card>

      {(reportData || superReportData) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {reportType === "⭐ SUPER REPORT - ΑΝΑΛΥΤΙΚΗ ΑΠΟΔΕΙΞΗ" && <FileText className="h-5 w-5" />}
              {reportType}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderReport()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
